// Based on https://github.com/yWorks/yfiles-for-html-demos/blob/58aa22c8e2fd95ee64893a59edef397bc0e1852f/demos/02-tutorial-custom-styles/25-custom-group-style/MyGroupNodeStyle.js
import {
  GeneralPath,
  GraphComponent,
  INode,
  IRenderContext,
  NodeStyleBase,
  SvgVisual,
} from '@ardoq/yfiles';
import {
  GroupNodeDefaults,
  HORIZONTAL_ICON_MARGIN,
  VERTICAL_ICON_MARGIN,
} from '../consts';
import { createSvgText, getRepresentationElement } from './utils';
import { RepresentationData } from '@ardoq/data-model';
import { createSvgElement } from '@ardoq/dom-utils';

type NodeData = {
  color: string;
  height: number;
  width: number;
  isCollapsed: boolean;
  equals: (self: NodeData, other: NodeData) => boolean;
};

type AqGroupNodeStyleProps = {
  color: string;
  representationData: RepresentationData | null;
  label: string;
  labelColor: string;
  iconColor: string;
};

/**
 * This group node style creates a visualization that is mainly a rectangle.
 * Additionally, it displays a textual description on the top left corner.
 * This implementation uses the convenience class {@link NodeStyleBase} as the
 * base class since this makes customizations easy.
 */
class AqGroupNodeStyle extends NodeStyleBase {
  $nodeColor: string;
  $representationData: RepresentationData | null;
  $iconColor: string;
  $label: string;
  $labelColor: string;

  constructor({
    color,
    representationData,
    iconColor,
    label,
    labelColor,
  }: AqGroupNodeStyleProps) {
    super();
    this.$nodeColor = color;
    this.$representationData = representationData;
    this.$iconColor = iconColor;
    this.$label = label;
    this.$labelColor = labelColor;
  }

  createVisual(context: IRenderContext, node: INode): SvgVisual {
    const g = createSvgElement('g');
    this.render(g, this.createRenderDataCache(node, context));
    SvgVisual.setTranslate(g, node.layout.x, node.layout.y);
    return new SvgVisual(g);
  }

  updateVisual(
    context: IRenderContext,
    oldVisual: SvgVisual,
    node: INode
  ): SvgVisual {
    const container = oldVisual.svgElement;
    // get the data with which the oldvisual was created
    const oldCache = (container as unknown as { [s: string]: NodeData })[
      'data-renderDataCache'
    ];
    // get the data for the new visual

    const newCache = this.createRenderDataCache(node, context);
    // check if something changed except for the location of the node
    if (!newCache.equals(newCache, oldCache)) {
      // something changed - re-render the visual
      while (container.hasChildNodes()) {
        container.removeChild(container.firstChild as Node);
      }
      this.render(container, newCache);
    }
    // make sure that the location is up to date
    SvgVisual.setTranslate(container, node.layout.x, node.layout.y);
    return oldVisual;
  }

  /**
   * Creates the actual visualization of this style and adds it to the given container.
   */
  render(container: SVGElement, cache: NodeData) {
    (container as unknown as { [s: string]: NodeData })[
      'data-renderDataCache'
    ] = cache;
    const width = cache.width;
    const height = cache.height;

    const outerPath = createPath(width, height);
    const outerPathElement = outerPath.createSvgPath();
    outerPathElement.setAttribute('fill', cache.color);
    container.appendChild(outerPathElement);

    const svgText = createSvgText({
      labelText: this.$label,
      labelColor: this.$labelColor,
      nodeWidth: width,
    });
    container.appendChild(svgText);

    if (!this.$representationData) return;

    const representationElement = getRepresentationElement(
      this.$representationData
    );

    representationElement.setAttribute('fill', this.$iconColor);
    representationElement.setAttribute('x', `${HORIZONTAL_ICON_MARGIN}`);
    representationElement.setAttribute('y', `${VERTICAL_ICON_MARGIN}`);

    container.appendChild(representationElement);
  }

  /**
   * Creates an object containing all necessary data to create a visual for the node.
   */
  createRenderDataCache(node: INode, context: IRenderContext): NodeData {
    return {
      color: this.nodeColor,
      width: node.layout.width,
      height: node.layout.height,
      isCollapsed: isCollapsed(node, context),
      equals: (self: NodeData, other: NodeData) =>
        self.color === other.color &&
        self.width === other.width &&
        self.height === other.height &&
        self.isCollapsed === other.isCollapsed,
    };
  }

  get nodeColor() {
    return this.$nodeColor;
  }

  set nodeColor(value: string) {
    this.$nodeColor = value;
  }
}

function isCollapsed(node: INode, context: IRenderContext) {
  if (!(context.canvasComponent instanceof GraphComponent)) {
    return false;
  }
  const foldedGraph = context.canvasComponent.graph.foldingView;
  if (foldedGraph === null) {
    return false;
  }
  return !foldedGraph.isExpanded(node);
}

/**
 * Creates the outer group path
 */
function createPath(width: number, height: number) {
  const path = new GeneralPath();
  path.moveTo(GroupNodeDefaults.BORDER_RADIUS, 0);
  path.lineTo(width - GroupNodeDefaults.BORDER_RADIUS, 0);
  path.quadTo(width, 0, width, GroupNodeDefaults.BORDER_RADIUS);
  path.lineTo(width, height - GroupNodeDefaults.BORDER_RADIUS);
  path.quadTo(width, height, width - GroupNodeDefaults.BORDER_RADIUS, height);
  path.lineTo(GroupNodeDefaults.BORDER_RADIUS, height);
  path.quadTo(0, height, 0, height - GroupNodeDefaults.BORDER_RADIUS);
  path.lineTo(0, GroupNodeDefaults.BORDER_RADIUS);
  path.quadTo(0, 0, GroupNodeDefaults.BORDER_RADIUS, 0);
  path.close();

  return path;
}

export { AqGroupNodeStyle };
